import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import Select from "react-select";
import "../../formFillComponents/DynamicFormComponents/FormInput.css";
import "./OrganizationWorkspace.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Building2, CheckCircle2, CreditCard, GitBranch, Shield, UserPlus, Users } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import { BrandLoader } from "../../../ui/Logo/BrandLoader";
import ConfirmationModal from "../../../modal/ConfimationModal/ConfirmationModal";
import {
  getOrganizationEmployees,
  getOrganizationConfiguration,
  getOwnerInvitations,
  getOrganizationWorkspace,
  inviteOrganizationEmployee,
  setOrganizationEmployeeActive,
  SuperAdminOrganizationDetail,
  SuperAdminOrganizationEmployee,
  SuperAdminOrganizationConfiguration,
  OrganizationInvitation,
  issueOwnerInvitation,
  resendOwnerInvitation,
  revokeOwnerInvitation,
  updateOrganization,
} from "../../../../services/superAdminServices/SuperAdminOrganizationService";

type WorkspaceTab = "overview" | "owner" | "employees" | "configuration";
type PendingConfirmation = {
  generation: number; organizationId: number; organizationName: string;
} & ({ kind: "employee"; employee: SuperAdminOrganizationEmployee; nextActive: boolean }
  | { kind: "invitation"; invitation: OrganizationInvitation });

const OrganizationWorkspace: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const targetOrganizationId = Number(organizationId);
  const validOrganizationId = Number.isInteger(targetOrganizationId) && targetOrganizationId > 0;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const routeContextRef = useRef({ organizationId: validOrganizationId ? targetOrganizationId : null, currentUser, generation: 0 });
  if (routeContextRef.current.organizationId !== (validOrganizationId ? targetOrganizationId : null) || routeContextRef.current.currentUser !== currentUser) {
    routeContextRef.current = {
      organizationId: validOrganizationId ? targetOrganizationId : null,
      currentUser,
      generation: routeContextRef.current.generation + 1,
    };
  }
  const [organization, setOrganization] = useState<SuperAdminOrganizationDetail | null>(null);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const operationRef = useRef<object | null>(null);
  const [employees, setEmployees] = useState<SuperAdminOrganizationEmployee[]>([]);
  const [configuration, setConfiguration] = useState<SuperAdminOrganizationConfiguration>({ roles: [], regions: [], waiverFamilies: [], teams: [] });
  const [ownerInvitations, setOwnerInvitations] = useState<OrganizationInvitation[]>([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [tab, setTab] = useState<WorkspaceTab>("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState({ name: "", abbreviation: "" });
  const [invite, setInvite] = useState({ firstName: "", lastName: "", email: "", roleId: 0, regionId: 0 });

  const isCurrentRoute = useCallback((generation: number, expectedOrganizationId: number) => {
    const context = routeContextRef.current;
    return context.generation === generation && context.organizationId === expectedOrganizationId;
  }, []);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!validOrganizationId) {
      setError("The organization ID is invalid.");
      setLoading(false);
      return;
    }
    if (!currentUser) {
      setError("You must be signed in to open an organization workspace.");
      setLoading(false);
      return;
    }
    const generation = routeContextRef.current.generation;
    setLoading(true);
    setError("");
    try {
      const token = await currentUser.getIdToken();
      if (signal?.aborted || !isCurrentRoute(generation, targetOrganizationId)) return;
      const [detail, employeeList, configurationData, invitations] = await Promise.all([
        getOrganizationWorkspace(token, targetOrganizationId, signal),
        getOrganizationEmployees(token, targetOrganizationId, signal),
        getOrganizationConfiguration(token, targetOrganizationId, signal),
        getOwnerInvitations(token, targetOrganizationId, signal),
      ]);
      if (signal?.aborted || !isCurrentRoute(generation, targetOrganizationId)) return;
      setOrganization(detail);
      setEmployees(employeeList);
      setConfiguration(configurationData);
      setOwnerInvitations(invitations);
      setProfile({ name: detail.name, abbreviation: detail.abbreviation });
    } catch (reason) {
      if (signal?.aborted || !isCurrentRoute(generation, targetOrganizationId)) return;
      setError(reason instanceof Error ? reason.message : "Unable to load this organization.");
      setOrganization(null);
      setEmployees([]);
    } finally {
      if (!signal?.aborted && isCurrentRoute(generation, targetOrganizationId)) setLoading(false);
    }
  }, [currentUser, isCurrentRoute, targetOrganizationId, validOrganizationId]);

  useEffect(() => {
    // Reset all tenant-specific state before loading a new route target.
    setConfirmation(null);
    setActionBusy(false);
    setOrganization(null);
    setEmployees([]);
    setConfiguration({ roles: [], regions: [], waiverFamilies: [], teams: [] });
    setOwnerInvitations([]);
    setOwnerEmail("");
    setSaving(false);
    setProfile({ name: "", abbreviation: "" });
    setInvite({ firstName: "", lastName: "", email: "", roleId: 0, regionId: 0 });
    setTab("overview");
    const controller = new AbortController();
    const generation = routeContextRef.current.generation;
    void load(controller.signal);
    return () => {
      controller.abort();
      if (routeContextRef.current.generation === generation) routeContextRef.current.generation += 1;
    };
  }, [load]);

  const saveProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (!currentUser || !organization) return;
    const generation = routeContextRef.current.generation;
    const operationOrganizationId = targetOrganizationId;
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      const updated = await updateOrganization(token, operationOrganizationId, {
        ...profile,
        atRiskWindowDays: organization.atRiskWindowDays,
        taskDelayDurationDays: organization.taskDelayDurationDays,
        allocatedPto: organization.allocatedPto,
      });
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      setOrganization(updated);
      setProfile({ name: updated.name, abbreviation: updated.abbreviation });
      toast.success(`${updated.name} was updated.`);
    } catch (reason) {
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      toast.error(reason instanceof Error ? reason.message : "Unable to update the organization.");
    } finally {
      if (isCurrentRoute(generation, operationOrganizationId)) setSaving(false);
    }
  };

  const sendInvite = async (event: FormEvent) => {
    event.preventDefault();
    if (!currentUser || !organization) return;
    const generation = routeContextRef.current.generation;
    const operationOrganizationId = targetOrganizationId;
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      await inviteOrganizationEmployee(token, operationOrganizationId, {
        firstName: invite.firstName,
        lastName: invite.lastName,
        displayName: `${invite.firstName} ${invite.lastName}`.trim(),
        email: invite.email,
        roleId: invite.roleId,
        regionIds: [invite.regionId],
        waiverFamilyIds: [],
        teamIds: [],
      });
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      const refreshedEmployees = await getOrganizationEmployees(token, operationOrganizationId);
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      toast.success(`Employee invitation sent to ${invite.email} for ${organization.name}.`);
      setInvite({ firstName: "", lastName: "", email: "", roleId: 0, regionId: 0 });
      setEmployees(refreshedEmployees);
    } catch (reason) {
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      toast.error(reason instanceof Error ? reason.message : "Unable to create the employee invitation.");
    } finally {
      if (isCurrentRoute(generation, operationOrganizationId)) setSaving(false);
    }
  };

  const sendOwnerInvite = async (event: FormEvent) => {
    event.preventDefault();
    if (!currentUser || !organization) return;
    const generation = routeContextRef.current.generation;
    const operationOrganizationId = targetOrganizationId;
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      await issueOwnerInvitation(token, operationOrganizationId, ownerEmail);
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      const refreshedInvitations = await getOwnerInvitations(token, operationOrganizationId);
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      setOwnerInvitations(refreshedInvitations);
      setOwnerEmail("");
      toast.success(`Owner invitation sent for ${organization.name}.`);
    } catch (reason) {
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      toast.error(reason instanceof Error ? reason.message : "Unable to issue the owner invitation.");
    } finally {
      if (isCurrentRoute(generation, operationOrganizationId)) setSaving(false);
    }
  };

  const updateOwnerInvitation = async (invitation: OrganizationInvitation, action: "resend" | "revoke") => {
    if (!currentUser || !organization || saving || operationRef.current) return;
    const generation = routeContextRef.current.generation;
    const operationOrganizationId = targetOrganizationId;
    if (action === "revoke") {
      setConfirmation({ kind: "invitation", invitation, generation, organizationId: operationOrganizationId, organizationName: organization.name });
      return;
    }
    const operation = {};
    operationRef.current = operation;
    setActionBusy(true);
    try {
      const token = await currentUser.getIdToken();
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      await resendOwnerInvitation(token, operationOrganizationId, invitation.id);
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      const refreshedInvitations = await getOwnerInvitations(token, operationOrganizationId);
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      setOwnerInvitations(refreshedInvitations);
      toast.success("Invitation resent with a new one-time token.");
    } catch (reason) {
      if (!isCurrentRoute(generation, operationOrganizationId)) return;
      toast.error(reason instanceof Error ? reason.message : `Unable to ${action} the invitation.`);
    } finally {
      if (operationRef.current === operation) operationRef.current = null;
      if (isCurrentRoute(generation, operationOrganizationId)) setActionBusy(false);
    }
  };

  const toggleEmployee = (employee: SuperAdminOrganizationEmployee) => {
    if (!currentUser || !organization || saving || operationRef.current || !employee.roles.length || employee.roles.some((role) => role.protected)) return;
    setConfirmation({ kind: "employee", employee, nextActive: employee.deactivated,
      generation: routeContextRef.current.generation, organizationId: targetOrganizationId, organizationName: organization.name });
  };

  const closeConfirmation = useCallback(() => {
    if (!operationRef.current) setConfirmation(null);
  }, []);

  const confirmAction = async () => {
    const pending = confirmation;
    if (!pending || !currentUser || saving || operationRef.current || !isCurrentRoute(pending.generation, pending.organizationId)) return;
    const targetIsCurrent = pending.kind === "employee"
      ? employees.some((employee) => employee.id === pending.employee.id && employee.deactivated === pending.employee.deactivated && employee.roles.length > 0 && !employee.roles.some((role) => role.protected))
      : ownerInvitations.some((invitation) => invitation.id === pending.invitation.id && invitation.organizationId === pending.organizationId && invitation.status !== "accepted");
    if (!targetIsCurrent) { setConfirmation(null); return; }
    const operation = {};
    operationRef.current = operation;
    setActionBusy(true);
    try {
      const token = await currentUser.getIdToken();
      if (!isCurrentRoute(pending.generation, pending.organizationId)) return;
      if (pending.kind === "employee") {
        const updated = await setOrganizationEmployeeActive(token, pending.organizationId, pending.employee, pending.nextActive);
        if (!isCurrentRoute(pending.generation, pending.organizationId)) return;
        setEmployees((current) => current.map((item) => item.id === updated.id ? updated : item));
      } else {
        await revokeOwnerInvitation(token, pending.organizationId, pending.invitation.id);
        if (!isCurrentRoute(pending.generation, pending.organizationId)) return;
        const refreshedInvitations = await getOwnerInvitations(token, pending.organizationId);
        if (!isCurrentRoute(pending.generation, pending.organizationId)) return;
        setOwnerInvitations(refreshedInvitations);
        toast.success("Invitation revoked.");
      }
      setConfirmation(null);
    } catch (reason) {
      if (!isCurrentRoute(pending.generation, pending.organizationId)) return;
      toast.error(reason instanceof Error ? reason.message : "Unable to complete this action.");
      // A partial failure may already have changed status. Reload before allowing another action.
      setConfirmation(null);
      await load();
    } finally {
      if (operationRef.current === operation) operationRef.current = null;
      if (isCurrentRoute(pending.generation, pending.organizationId)) setActionBusy(false);
    }
  };

  if (!validOrganizationId) return (
    <div className="organization-workspace-theme organization-workspace-error m-6 rounded-xl border border-red-200 bg-red-50 p-6 text-red-800" role="alert">
      <h1 className="text-lg font-semibold">Organization workspace unavailable</h1>
      <p className="mt-2">The organization ID is invalid.</p>
      <button className="mt-4 font-semibold underline" onClick={() => navigate("/dashboard/superadmin")}>Return to organizations</button>
    </div>
  );

  const organizationMatchesRoute = organization?.id === targetOrganizationId;
  if (loading || (!error && organization !== null && !organizationMatchesRoute)) return <div className="organization-workspace-theme organization-workspace-loading flex min-h-[55vh] items-center justify-center" role="status" aria-label="Loading organization workspace"><BrandLoader /></div>;
  if (error || !organization || !organizationMatchesRoute) return (
    <div className="organization-workspace-theme organization-workspace-error m-6 rounded-xl border border-red-200 bg-red-50 p-6 text-red-800" role="alert">
      <h1 className="text-lg font-semibold">Organization workspace unavailable</h1>
      <p className="mt-2">{error || "The organization could not be found."}</p>
      <button className="mt-4 font-semibold underline" onClick={() => navigate("/dashboard/superadmin")}>Return to organizations</button>
    </div>
  );

  return (
    <main className="organization-workspace-theme min-h-screen bg-gray-50 p-4 md:p-6">
      <button onClick={() => navigate("/dashboard/superadmin")} className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-purple-700">
        <ArrowLeft className="h-4 w-4" /> Organizations
      </button>
      <header className="rounded-xl border border-purple-200 bg-white shadow-sm">
        <div className="border-b border-purple-100 bg-purple-50 px-5 py-2 text-sm font-semibold text-purple-900">
          Editing organization: {organization.name} · ID {organization.id}
        </div>
        <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-100 p-3 text-purple-700"><Building2 className="h-6 w-6" /></div>
            <div><h1 className="text-2xl font-bold text-gray-900">{organization.name}</h1><p className="text-sm text-gray-500">{organization.abbreviation} · {organization.onboardingStatus}</p></div>
          </div>
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${organization.readiness.isReady ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
            {organization.readiness.isReady ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {organization.readiness.isReady ? "Ready" : `${organization.readiness.findings.length} readiness item(s)`}
          </span>
        </div>
        <nav aria-label="Organization workspace" className="flex gap-1 overflow-x-auto border-t border-gray-100 px-4">
          {(["overview", "owner", "employees", "configuration"] as WorkspaceTab[]).map((item) => (
            <button key={item} aria-current={tab === item ? "page" : undefined} onClick={() => setTab(item)} className={`border-b-2 px-4 py-3 text-sm font-semibold capitalize ${tab === item ? "border-purple-600 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-800"}`}>{item}</button>
          ))}
        </nav>
      </header>

      {tab === "overview" && <section className="mt-5 grid gap-5 lg:grid-cols-2">
        <form onSubmit={saveProfile} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">Organization profile</h2>
          <p className="mt-1 text-sm text-gray-500">Changes apply only to {organization.name}.</p>
          <label className="mt-5 block text-sm font-semibold text-gray-700">Name<input required value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} className="form-v2-input mt-1 w-full" /></label>
          <label className="mt-4 block text-sm font-semibold text-gray-700">Abbreviation<input required value={profile.abbreviation} onChange={(event) => setProfile({ ...profile, abbreviation: event.target.value.toUpperCase() })} className="form-v2-input mt-1 w-full font-mono" /></label>
          <button disabled={saving} className="mt-5 rounded-md bg-purple-700 px-4 py-2 font-semibold text-white disabled:opacity-50">Save changes for {organization.name}</button>
        </form>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">Onboarding readiness</h2>
          {organization.readiness.findings.length ? <ul className="mt-4 space-y-2">{organization.readiness.findings.map((finding) => <li key={finding} className="flex gap-2 text-sm text-amber-900"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{finding}</li>)}</ul> : <p className="mt-4 flex gap-2 text-sm text-emerald-800"><CheckCircle2 className="h-4 w-4" />All server validation checks pass.</p>}
          <dl className="mt-6 grid grid-cols-2 gap-3 text-sm"><div className="rounded-lg bg-gray-50 p-3"><dt className="text-gray-500">Employees</dt><dd className="text-xl font-bold">{organization.activeUserCount}</dd></div><div className="rounded-lg bg-gray-50 p-3"><dt className="text-gray-500">Roles</dt><dd className="text-xl font-bold">{organization.roleCount}</dd></div><div className="rounded-lg bg-gray-50 p-3"><dt className="text-gray-500">Workflows</dt><dd className="text-xl font-bold">{organization.workflowDefinitionCount}</dd></div><div className="rounded-lg bg-gray-50 p-3"><dt className="text-gray-500">Assignments</dt><dd className="text-xl font-bold">{organization.workflowAssignmentCount}</dd></div></dl>
        </div>
      </section>}

      {tab === "owner" && <section className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5"><h2 className="text-lg font-bold">Owner invitations</h2><p className="text-sm text-gray-500">Tokens are one-time, expire automatically, and are never displayed here.</p></div>
          <div className="overflow-x-auto"><table className="w-full"><thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Expires</th><th className="p-3 text-right">Actions</th></tr></thead><tbody>{ownerInvitations.map((invitation) => <tr key={invitation.id} className="border-t border-gray-100"><td className="p-3 font-medium">{invitation.email}</td><td className="p-3 text-sm capitalize">{invitation.status}</td><td className="p-3 text-sm">{new Date(invitation.expiresAt).toLocaleString()}</td><td className="p-3 text-right">{invitation.status !== "accepted" && <><button disabled={saving || actionBusy} onClick={() => void updateOwnerInvitation(invitation, "resend")} className="mr-3 text-sm font-semibold text-purple-700 hover:underline">Resend</button><button disabled={saving || actionBusy} onClick={() => void updateOwnerInvitation(invitation, "revoke")} className="text-sm font-semibold text-red-700 hover:underline">Revoke</button></>}</td></tr>)}</tbody></table>{!ownerInvitations.length && <p className="p-8 text-center text-gray-500">No owner invitation has been issued.</p>}</div>
        </div>
        <form onSubmit={sendOwnerInvite} className="h-fit rounded-xl border border-gray-200 bg-white p-5 shadow-sm"><h2 className="text-lg font-bold">Invite organization owner</h2><p className="mt-1 text-sm text-gray-500">This replaces any active owner invitation for {organization.name}.</p><label className="mt-4 block text-sm font-semibold">Owner email<input required type="email" value={ownerEmail} onChange={(event) => setOwnerEmail(event.target.value)} className="form-v2-input mt-1 w-full" /></label><button disabled={saving} className="mt-4 w-full rounded-md bg-purple-700 px-4 py-2 font-semibold text-white disabled:opacity-50">Send owner invitation</button></form>
      </section>}

      {tab === "employees" && <section className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"><div className="border-b border-gray-100 p-5"><h2 className="text-lg font-bold">Employees</h2><p className="text-sm text-gray-500">Every result is scoped to {organization.name}.</p></div><div className="overflow-x-auto"><table className="w-full"><thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr><th className="p-3">Employee</th><th className="p-3">Roles</th><th className="p-3">Status</th><th className="p-3 text-right">Action</th></tr></thead><tbody>{employees.map((employee) => <tr key={employee.id} className="border-t border-gray-100"><td className="p-3"><div className="font-semibold">{employee.displayName}</div><div className="text-sm text-gray-500">{employee.email}</div></td><td className="p-3 text-sm">{employee.roles.map((role) => role.name).join(", ") || "No role"}</td><td className="p-3 text-sm">{employee.deactivated ? "Disabled" : "Active"}</td><td className="p-3 text-right"><button disabled={saving || actionBusy || !employee.roles.length || employee.roles.some((role) => role.protected)} onClick={() => void toggleEmployee(employee)} className="text-sm font-semibold text-purple-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-400">{employee.deactivated ? "Enable" : "Disable"}</button></td></tr>)}</tbody></table>{!employees.length && <p className="p-8 text-center text-gray-500">No employees found.</p>}</div></div>
        <form onSubmit={sendInvite} className="h-fit rounded-xl border border-gray-200 bg-white p-5 shadow-sm"><h2 className="flex items-center gap-2 text-lg font-bold"><UserPlus className="h-5 w-5" />Invite employee</h2><p className="mt-1 text-sm text-gray-500">The account and every selected reference will be bound to {organization.name} after the employee accepts.</p><label className="mt-4 block text-sm font-semibold">First name<input required value={invite.firstName} onChange={(event) => setInvite({ ...invite, firstName: event.target.value })} className="form-v2-input mt-1 w-full" /></label><label className="mt-3 block text-sm font-semibold">Last name<input required value={invite.lastName} onChange={(event) => setInvite({ ...invite, lastName: event.target.value })} className="form-v2-input mt-1 w-full" /></label><label className="mt-3 block text-sm font-semibold">Email<input required type="email" value={invite.email} onChange={(event) => setInvite({ ...invite, email: event.target.value })} className="form-v2-input mt-1 w-full" /></label><div className="mt-3"><label htmlFor="organization-invite-role" className="block text-sm font-semibold">Role</label><Select inputId="organization-invite-role" name="roleId" required isDisabled={saving} className="form-v2-react-select react-select-container mt-1" classNamePrefix="react-select" placeholder="Select role" options={configuration.roles.filter((role) => !role.protected).map((role) => ({ value: role.id, label: role.name }))} value={configuration.roles.filter((role) => !role.protected && role.id === invite.roleId).map((role) => ({ value: role.id, label: role.name }))[0] || null} onChange={(option) => setInvite({ ...invite, roleId: option?.value || 0 })} /></div><div className="mt-3"><label htmlFor="organization-invite-region" className="block text-sm font-semibold">Primary region</label><Select inputId="organization-invite-region" name="regionId" required isDisabled={saving} className="form-v2-react-select react-select-container mt-1" classNamePrefix="react-select" placeholder="Select region" options={configuration.regions.map((region) => ({ value: region.id, label: region.name }))} value={configuration.regions.filter((region) => region.id === invite.regionId).map((region) => ({ value: region.id, label: region.name }))[0] || null} onChange={(option) => setInvite({ ...invite, regionId: option?.value || 0 })} /></div><button disabled={saving} className="mt-4 w-full rounded-md bg-purple-700 px-4 py-2 font-semibold text-white disabled:opacity-50">Send employee invitation</button></form>
      </section>}

      {tab === "configuration" && <section className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <WorkspaceLink icon={<Shield />} title="Roles and permissions" description="Create roles, assign employees, and manage policies." to={`/superadmin/roles/${organization.id}`} />
        <WorkspaceLink icon={<GitBranch />} title="Workflows" description="Edit definitions and active organization assignments." to={`/superadmin/workflow/${organization.id}`} />
        <WorkspaceLink icon={<CreditCard />} title="Billing" description="Review plan, payment methods, and billing history." to={`/dashboard/superadmin/billing-details/${organization.id}`} />
        <WorkspaceLink icon={<Users />} title="Employees" description="Return to the scoped employee workspace." onClick={() => setTab("employees")} />
      </section>}
      <ConfirmationModal
        isOpen={!!confirmation && isCurrentRoute(confirmation.generation, confirmation.organizationId)}
        onClose={closeConfirmation} onConfirm={confirmAction} closeOnConfirm={false}
        isLoading={actionBusy} confirmDisabled={saving}
        title={confirmation?.kind === "employee" ? `${confirmation.nextActive ? "Enable" : "Disable"} ${confirmation.employee.displayName}?` : "Revoke owner invitation?"}
        confirmButtonText={confirmation?.kind === "employee" ? (confirmation.nextActive ? "Enable account" : "Disable account") : "Revoke invitation"}
        text={confirmation?.kind === "employee"
          ? <p>{confirmation.nextActive ? "Restore account access" : "Stop account access and end active sessions"} for {confirmation.employee.displayName} in {confirmation.organizationName}.</p>
          : <p>Revoke the owner invitation for {confirmation?.invitation.email} in {confirmation?.organizationName}. The invitation will no longer grant access.</p>}
      />
    </main>
  );
};

const WorkspaceLink = ({ icon, title, description, to, onClick }: { icon: React.ReactNode; title: string; description: string; to?: string; onClick?: () => void }) => {
  const classes = "organization-workspace-link block rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:border-purple-300 hover:shadow-md";
  const content = <><span className="text-purple-700">{icon}</span><h2 className="mt-3 font-bold text-gray-900">{title}</h2><p className="mt-1 text-sm text-gray-500">{description}</p></>;
  return to ? <Link className={classes} to={to}>{content}</Link> : <button className={classes} onClick={onClick}>{content}</button>;
};

export default OrganizationWorkspace;
